import { getErrorMessage } from 'shared/api/helpers/error-helper';
import { config } from 'shared/config';
import { IServicesCard } from '../interfaces/services-list.interface';
import { BookingErrors } from '../consts/booking.error';
import { IGetServicesListDto } from '../interfaces/get-services-list-dto.interface';
import { instance } from 'shared/api/instance';
import { IServiceCategory } from '../interfaces/service-categories.interface';
import { IGetServiceCategoriesParams } from '../interfaces/get-service-categories.params.interface';
import { ICreateServiceParams } from '../interfaces/create-service.params.interface';
import { IGetCalendarCardMonthParams } from '../interfaces/get-calendar-card-month.params.interface';
import { IGetMonthInfo } from '../interfaces/calendar-card-month.interface';
import { IWorkDayRuleCreateParams } from '../interfaces/work-day-rule-create.params.interface';
import { IGetWorkTimeRulesParams } from '../interfaces/get-work-time-rules.params.interface';
import { IWorkTimeRules } from '../interfaces/work-time-rules.interface';
import { IWorkDayRuleDeleteParams } from '../interfaces/work-day-rule-delete.params.interface';
import { IWorkBreakRuleDeleteParams } from '../interfaces/work-break-rule-delete.params.interface';
import { IWorkBreakRuleCreateParams } from '../interfaces/work-break-rule-create.params.interface';
import { IGetDayFreeSlotsParams } from '../interfaces/get-day-free-slots.params.interface';
import { IDayFreeSlots } from '../interfaces/day-free-slots.interface';
import { ICreateBookingParams } from '../interfaces/create-booking.params.interface';
import { CalendarDayType } from '../interfaces/calendar-day.type';
import { IGetCalendarDayParams } from '../interfaces/get-calendar-day.params.interface';
import { IGetCalendarBookingParams } from '../interfaces/get-calendar-booking.params.interface';
import { ICalendarBookingType } from '../interfaces/calendar-booking-type.interface';
import { ICancelBookingParams } from '../interfaces/cancel-booking.params.interface';
import { IGetCalendarWeekInfoParams } from '../interfaces/get-calendar-week-info.params.interface';
import { ICalendarWeekInfoType } from '../interfaces/calendar-week-info-type.interface';
import { IGetCalendarBreakParams } from '../interfaces/get-calendar-break.params.interface';
import { ICalendarBreakType } from '../interfaces/calendar-break-type.interface';
import { ICreateServiceCategoryParams } from '../interfaces/create-service-category.params.inteface';
import { IDeleteServiceCategoryParams } from '../interfaces/delete-service-category.params.interface';
import { IUpdateServiceCategoryParams } from '../interfaces/update-service-category.params.interface';
import { IEditServiceParams } from '../interfaces/edit-service.params.interface';
import { IDeleteServiceParams } from '../interfaces/delete-service.params.interface';
import { IDayOverrideParams } from '../interfaces/day-override.params.interface';
import { IBreakOverrideParams } from '../interfaces/break-override.params.interface';
import { IUpdateBookingParams } from '../interfaces/update-booking.params.interface';
import { IUpdateBreakOverrideParams } from '../interfaces/update-breakOverride.params.interface';
import {
  IGetEmployeesMonthScheduleParams,
  IEmployeesMonthSchedule,
} from '../interfaces/get-employees-month-schedule.interface';
import * as dateFns from 'date-fns';
import { DATE_FORMAT } from 'shared/consts/date-format';

export enum WorkDayTypeEnum {
  WORK_DAY = 'workDay',
  DAY_OFF = 'dayOff',
  VACATION = 'vacation',
  SICK_LEAVE = 'sickLeave',
  ADMINISTRATIVE = 'administrative',
}

const API = instance(config.API_BOOKING);

export class BookingApi {
  public static async getServiceList(
    dto: IGetServicesListDto
  ): Promise<IServicesCard[]> {
    try {
      const url = '/service/list';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.GET_SERVICES_LIST));
    }
  }

  public static async getServiceCategories(
    dto: IGetServiceCategoriesParams
  ): Promise<IServiceCategory[]> {
    try {
      const url = '/service-category/' + dto.pointId;
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.GET_SERVICES_LIST));
    }
  }

  public static async getCalendarMonthInfo(
    dto: IGetCalendarCardMonthParams
  ): Promise<IGetMonthInfo> {
    try {
      const url = '/work-time/month-info';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_CALENDAR_MONTH_INFO)
      );
    }
  }

  public static async getEmployeesMonthSchedule(
    dto: IGetEmployeesMonthScheduleParams
  ): Promise<IEmployeesMonthSchedule> {
    try {
      const url = '/work-time/employees-month-schedule';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_EMPLOYEES_MONTH_SCHEDULE)
      );
    }
  }

  public static async getWorkTimeRules(
    dto: IGetWorkTimeRulesParams
  ): Promise<IWorkTimeRules> {
    try {
      const url = '/work-time/rules';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_WORK_TIME_RULES)
      );
    }
  }

  public static async getDayFreeSlots(
    dto: IGetDayFreeSlotsParams
  ): Promise<IDayFreeSlots> {
    try {
      const url = '/work-time/day-free-slots';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_DAY_FREE_SLOTS_RULES)
      );
    }
  }

  public static async getCalendarDay(
    dto: IGetCalendarDayParams
  ): Promise<CalendarDayType> {
    try {
      const url = '/calendar/day';
      return (
        await API.get(url, {
          params: { ...dto, date: dateFns.format(dto.date, DATE_FORMAT) },
        })
      ).data;
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.GET_CALENDAR_DAY));
    }
  }

  public static async getCalendarBooking(
    dto: IGetCalendarBookingParams
  ): Promise<ICalendarBookingType> {
    try {
      const url = '/calendar/booking/' + dto.bookingId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_CALENDAR_BOOKING)
      );
    }
  }
  public static async getCalendarBreak(
    dto: IGetCalendarBreakParams
  ): Promise<ICalendarBreakType> {
    try {
      const url = '/calendar/break/' + dto.breakId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.GET_CALENDAR_BREAK));
    }
  }
  public static async getCalendarWeekInfo(
    dto: IGetCalendarWeekInfoParams
  ): Promise<ICalendarWeekInfoType> {
    try {
      const url = '/calendar/week-info';
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.GET_CALENDAR_WEEK_INFO)
      );
    }
  }

  public static async createService(dto: ICreateServiceParams): Promise<void> {
    try {
      const url = '/service';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.CREATE_SERVICE));
    }
  }
  public static async editService(dto: IEditServiceParams): Promise<void> {
    try {
      const url = '/service';
      return await API.patch(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.EDIT_SERVICE));
    }
  }

  public static async createServiceCategory(
    dto: ICreateServiceCategoryParams
  ): Promise<void> {
    try {
      const url = '/service-category';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.CREATE_SERVICE_CATEGORY)
      );
    }
  }
  public static async createBooking(dto: ICreateBookingParams): Promise<void> {
    try {
      const url = '/booking';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.CREATE_BOOKING));
    }
  }
  public static async deleteBooking(dto: ICancelBookingParams): Promise<void> {
    try {
      const url = '/booking/' + dto.bookingId + '/cancel';
      return await API.patch(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.DELETE_BOOKING));
    }
  }
  public static async deleteService(dto: IDeleteServiceParams): Promise<void> {
    try {
      const url = '/service/' + dto.serviceId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.DELETE_SERVICE));
    }
  }
  public static async deleteServiceCategory(
    dto: IDeleteServiceCategoryParams
  ): Promise<void> {
    try {
      const url = '/service-category/' + dto.categoryId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.DELETE_SERVICE_CATEGORY)
      );
    }
  }

  public static async workDayRuleCreate(
    dto: IWorkDayRuleCreateParams
  ): Promise<void> {
    try {
      const url = '/work-time/work-day-rule';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.WORK_DAY_CREATE));
    }
  }
  public static async workBreakRuleCreate(
    dto: IWorkBreakRuleCreateParams
  ): Promise<void> {
    try {
      const url = '/work-time/work-break-rule';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.WORK_BREAK_CREATE));
    }
  }
  public static async workDayRuleDelete(
    dto: IWorkDayRuleDeleteParams
  ): Promise<void> {
    try {
      const url = '/work-time/work-day-rule/' + dto.workDayId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.WORK_DAY_DELETE));
    }
  }

  public static async workBreakRuleDelete(
    dto: IWorkBreakRuleDeleteParams
  ): Promise<void> {
    try {
      const url = '/work-time/work-break-rule/' + dto.workBreakId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.WORK_BREAK_DELETE));
    }
  }

  public static async updateServiceCategory(
    dto: IUpdateServiceCategoryParams
  ): Promise<void> {
    try {
      const url = '/service-category/' + dto.categoryId;
      return await API.patch(url, { category: dto.category });
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.UPDATE_SERVICE_CATEGORY)
      );
    }
  }

  public static async dayOverride(dto: IDayOverrideParams): Promise<void> {
    try {
      const url = '/work-time/day-override';
      const { type, startedAt, finishedAt, ...base } = dto;
      const requestBody = type
        ? { ...base, type, startedAt, finishedAt }
        : { ...base, startedAt, finishedAt };
      return await API.post(url, requestBody);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.DAY_OVERRIDE));
    }
  }

  public static async breakOverride(dto: IBreakOverrideParams): Promise<void> {
    try {
      const url = '/work-time/break-override';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.BREAK_OVERRIDE));
    }
  }

  public static async updateBooking(dto: IUpdateBookingParams): Promise<void> {
    try {
      const { bookingId, ...updateData } = dto;
      const url = '/booking/' + bookingId;
      return await API.patch(url, updateData);
    } catch (error) {
      throw new Error(getErrorMessage(error, BookingErrors.UPDATE_BOOKING));
    }
  }

  public static async updateBreakOverride(
    breakOverrideId: string,
    dto: Omit<IUpdateBreakOverrideParams, 'breakOverrideId'>
  ): Promise<void> {
    try {
      const url = '/work-time/break-override/' + breakOverrideId;
      return await API.patch(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.UPDATE_BREAK_OVERRIDE)
      );
    }
  }

  public static async deleteBreakOverride(
    breakOverrideId: string,
    date: Date
  ): Promise<void> {
    try {
      const url =
        '/work-time/break-override/' +
        breakOverrideId +
        '?date=' +
        dateFns.format(date, DATE_FORMAT);
      return await API.delete(url);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BookingErrors.DELETE_BREAK_OVERRIDE)
      );
    }
  }
}
